/**
 * Bookmark Storage Utilities
 * Gestion du cache local des bookmarks pour une meilleure UX
 */

import type { Bookmark, BookmarkCache } from "../types/bookmark.types"

const STORAGE_KEY = "uic_bookmarks_cache"
const CACHE_DURATION = 5 * 60 * 1000 // 5 minutes

/**
 * Vérifie si le localStorage est disponible
 */
function isLocalStorageAvailable(): boolean {
  try {
    const test = "__localStorage_test__"
    localStorage.setItem(test, test)
    localStorage.removeItem(test)
    return true
  } catch {
    return false
  }
}

/**
 * Récupère les bookmarks du cache local
 */
export function getCachedBookmarks(userEmail: string): Bookmark[] | null {
  if (!isLocalStorageAvailable()) {
    return null
  }

  try {
    const cached = localStorage.getItem(STORAGE_KEY)
    if (!cached) {
      return null
    }

    const cache: BookmarkCache = JSON.parse(cached)

    // Vérifier que c'est bien le même utilisateur
    if (cache.userEmail !== userEmail) {
      localStorage.removeItem(STORAGE_KEY)
      return null
    }

    // Vérifier que le cache n'est pas expiré
    const now = Date.now()
    if (now - cache.timestamp > CACHE_DURATION) {
      localStorage.removeItem(STORAGE_KEY)
      return null
    }

    return cache.bookmarks
  } catch (error) {
    console.error("Error reading bookmarks cache:", error)
    return null
  }
}

/**
 * Sauvegarde les bookmarks dans le cache local
 */
export function setCachedBookmarks(
  userEmail: string,
  bookmarks: Bookmark[]
): void {
  if (!isLocalStorageAvailable()) {
    return
  }

  try {
    const cache: BookmarkCache = {
      bookmarks,
      timestamp: Date.now(),
      userEmail,
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(cache))
  } catch (error) {
    console.error("Error saving bookmarks cache:", error)
  }
}

/**
 * Ajoute un bookmark au cache (optimistic update)
 */
export function addToCache(userEmail: string, bookmark: Bookmark): void {
  const cached = getCachedBookmarks(userEmail)
  if (!cached) {
    setCachedBookmarks(userEmail, [bookmark])
    return
  }

  // Vérifier que le bookmark n'existe pas déjà
  const exists = cached.some((b) => b.id === bookmark.id)
  if (!exists) {
    setCachedBookmarks(userEmail, [...cached, bookmark])
  }
}

/**
 * Supprime un bookmark du cache (optimistic update)
 */
export function removeFromCache(
  userEmail: string,
  entityType: string,
  entityId: string
): void {
  const cached = getCachedBookmarks(userEmail)
  if (!cached) {
    return
  }

  const filtered = cached.filter(
    (b) => !(b.entityType === entityType && b.entityId === entityId)
  )

  setCachedBookmarks(userEmail, filtered)
}

/**
 * Supprime un bookmark du cache par son ID
 */
export function removeFromCacheById(userEmail: string, bookmarkId: string): void {
  const cached = getCachedBookmarks(userEmail)
  if (!cached) {
    return
  }

  const filtered = cached.filter((b) => b.id !== bookmarkId)
  setCachedBookmarks(userEmail, filtered)
}

/**
 * Vérifie si un item est dans le cache
 */
export function isInCache(
  userEmail: string,
  entityType: string,
  entityId: string
): boolean {
  const cached = getCachedBookmarks(userEmail)
  if (!cached) {
    return false
  }

  return cached.some(
    (b) => b.entityType === entityType && b.entityId === entityId
  )
}

/**
 * Vide le cache (utile lors de la déconnexion)
 */
export function clearCache(): void {
  if (!isLocalStorageAvailable()) {
    return
  }

  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch (error) {
    console.error("Error clearing bookmarks cache:", error)
  }
}

/**
 * Récupère le nombre de bookmarks en cache
 */
export function getCachedCount(userEmail: string): number {
  const cached = getCachedBookmarks(userEmail)
  return cached?.length || 0
}

/**
 * Merge les données du serveur avec le cache local
 * Privilégie toujours les données serveur en cas de conflit
 */
export function mergeCacheWithServer(
  userEmail: string,
  serverBookmarks: Bookmark[]
): void {
  setCachedBookmarks(userEmail, serverBookmarks)
}
